// src/api/invoiceApi.ts

import Repository from './Repository';
import {Invoice} from "@/models/Invoice/Invoice";
import {InvoiceRecipeCost} from "@/models/Invoice/InvoiceRecipeCost";
import {InvoiceIngredientCost} from "@/models/Invoice/InvoiceIngredientCost";
import {InvoiceAdditionalCost} from "@/models/Invoice/InvoiceAdditionalCost";
import {CreateRecipe} from "@/models/Recipes/CreateRecipe";
import {AxiosResponse} from "axios";
import {Recipe} from "@/models/Recipes/Recipe";
import {AdditionalCost} from "@/models/AdditionalCost/AdditionalCost";
import {IngredientCost} from "@/models/IngredientCost/IngredientCost";

export const resource = '/invoice';

export default {
  // Get all invoices
  getInvoices(): Promise<AxiosResponse<Invoice[]>> {
    return Repository.get(`${resource}/GetAllInvoices`, { withCredentials: true });
  },

  // Get invoice details
  getInvoiceDetails(invoiceId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.get(`${resource}/${invoiceId}/Details`, { withCredentials: true });
  },

  // Create new invoice
  createInvoice(invoiceName: string): Promise<AxiosResponse<Invoice>> {
    return Repository.post(`${resource}/Create`, { name: invoiceName }, { withCredentials: true });
  },

  // Update invoice name.
  updateInvoice(invoiceId: number, invoiceName: string): Promise<AxiosResponse<Invoice>> {
    return Repository.post(`${resource}/${invoiceId}/UpdateInvoice`, { name: invoiceName }, { withCredentials: true });
  },

  // Add recipe cost
  addRecipeCost(invoiceId: number, recipeCostId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.post(`${resource}/${invoiceId}/AddRecipeCost`, { costId :recipeCostId }, { withCredentials: true });
  },

  // Add ingredient cost
  addIngredientCost(invoiceId: number, ingredientCostId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.post(`${resource}/${invoiceId}/AddIngredientCost`, { costId:ingredientCostId }, { withCredentials: true });
  },

  // Add additional cost
  addAdditionalCost(invoiceId: number, additionalCostId: number): Promise<AxiosResponse<Invoice>>{
    return Repository.post(`${resource}/${invoiceId}/AddAdditionalCost`, { costId: additionalCostId }, { withCredentials: true });
  },

  // Remove recipe cost
  removeRecipeCost(invoiceId: number, recipeCostId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.post(`${resource}/${invoiceId}/RemoveRecipeCost`, { costId : recipeCostId }, { withCredentials: true });
  },

  // Remove ingredient cost
  removeIngredientCost(invoiceId: number, ingredientCostId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.post(`${resource}/${invoiceId}/RemoveIngredientCost`, { costId: ingredientCostId }, { withCredentials: true });
  },

  // Remove additional cost
  removeAdditionalCost(invoiceId: number, additionalCostId: number): Promise<AxiosResponse<Invoice>>{
    return Repository.post(`${resource}/${invoiceId}/RemoveAdditionalCost`, { costId : additionalCostId }, { withCredentials: true });
  },

  // Update recipe cost quantity
  updateRecipeCostQuantity(invoiceId: number, recipeCostId: number, quantity: number, measurementId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.put(
      `${resource}/${invoiceId}/UpdateRecipeCostQuantity`,
      { costId: recipeCostId, quantity, selectedMeasurementId: measurementId },
      { withCredentials: true }
    );
  },

  // Update ingredient cost quantity
  updateIngredientCostQuantity(invoiceId: number, ingredientCostId: number, quantity: number, measurementId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.put(
      `${resource}/${invoiceId}/UpdateIngredientCostQuantity`,
      { costId: ingredientCostId, quantity, selectedMeasurementId: measurementId },
      { withCredentials: true }
    );
  },

  // Update additional cost quantity
  updateAdditionalCostQuantity(invoiceId: number, additionalCostId: number, quantity: number, measurementId: number): Promise<AxiosResponse<Invoice>> {
    return Repository.put(
      `${resource}/${invoiceId}/UpdateAdditionalCostQuantity`,
      { costId: additionalCostId, quantity, selectedMeasurementId: measurementId },
      { withCredentials: true }
    );
  },

  // Delete recipe cost
  deleteRecipeCost(id: number): Promise<void> {
    return Repository.delete(`${resource}/recipe-cost/${id}`);
  },

  // Delete ingredient cost
  deleteIngredientCost(id: number): Promise<void> {
    return Repository.delete(`${resource}/ingredient-cost/${id}`);
  },

  // Delete additional cost
  deleteAdditionalCost(id: number): Promise<void> {
    return Repository.delete(`${resource}/additional-cost/${id}`);
  },

  // Delete multiple invoices
  deleteInvoices(invoiceIds: number[]): Promise<void> {
    return Repository.post(`${resource}/DeleteInvoices`, invoiceIds, { withCredentials: true });
  },

  getIngredientCostById(ingredientCostId: number): Promise<AxiosResponse<IngredientCost>> {
    return Repository.get(`${resource}/GetIngredientCostById/${ingredientCostId}`, { withCredentials: true });
  },
};